import React, { useState } from 'react';
import { Modal, Card, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faEnvelope, 
  faExternalLinkAlt, 
  faPaperPlane,
  faReply,
  faForward,
  faPaperclip,
  faBrain,
  faCalendarAlt,
  faClock,
  faFileAlt,
  faFilePdf,
  faFileImage,
  faFileArchive,
  faFileCode,
  faFileExcel,
  faFilePowerpoint,
  faFileWord,
  faFile,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { 
  UilMessage
} from "@iconscout/react-unicons";
import { ActivityTimelineData } from 'data/project-management/activityTimelineData';
import classNames from 'classnames';
import { useForm, SubmitHandler } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import axiosInstance from 'helpers/api';
import { formatErrorForReact } from 'helpers/utils';
import { decodeEmailBody } from 'helpers/email';
import './EmailDetailModal.css'; // Import responsive styles for email detail modal

interface EmailDetailModalProps {
  show: boolean;
  onHide: () => void;
  activityData: ActivityTimelineData | null;
  originalActivityItem?: any; // Add original activity data for threadId and other metadata
  isLoadingDetails?: boolean; // Add loading state for email details
}

type EmailFormInputs = {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  message: string;
  schedule_date?: string;
  attachments?: FileList;
};

interface EmailRecipient {
  email: string;
  name?: string;
  initials?: string;
}

interface EmailAttachment {
  id?: string;
  filename?: string;
  mimeType?: string;
  size?: number;
  attachmentId?: string;
  emailId?: string;
  // Additional fields for Gmail attachments
  name?: string;
  data?: string;
  // Loading state flag
  isLoading?: boolean;
}

interface EmailContent {
  subject: string;
  from: string;
  to: string;
  body: string;
  body_compressed?: string;
  isCompressed?: boolean;
  cc?: string;
  bcc?: string;
  attachments?: EmailAttachment[];
  mail_id?: string;
  thread_id?: string;
}

interface RecipientChipProps {
  recipient: EmailRecipient;
  onRemove: () => void;
  variant?: 'primary' | 'secondary' | 'success';
}

const RecipientChip: React.FC<RecipientChipProps> = ({ recipient, onRemove, variant = 'primary' }) => {
  const getInitials = (email: string, name?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const initials = recipient.initials || getInitials(recipient.email, recipient.name);
  const displayName = recipient.name || recipient.email.split('@')[0];

  return (
    <div className={classNames(
      'd-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill border me-1 mb-1',
      `border-${variant}`,
      'recipient-chip'
    )} style={{ 
      backgroundColor: `var(--bs-${variant}-bg-subtle)`, 
      fontSize: '0.8rem',
      lineHeight: '1.2'
    }}>
      <div 
        className={classNames(
          'd-flex align-items-center justify-content-center rounded-circle text-white fw-bold',
          `bg-${variant}`
        )}
        style={{ width: '18px', height: '18px', fontSize: '0.6rem' }}
      >
        {initials}
      </div>
      <span className="text-truncate" style={{ fontSize: '0.8rem', maxWidth: '100px' }}>
        {displayName}
      </span>
      <Button
        variant="link"
        size="sm"
        className="p-0 text-muted"
        onClick={onRemove}
        style={{ fontSize: '0.7rem', width: '14px', height: '14px' }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </Button>
    </div>
  );
};

interface RecipientInputProps {
  label: string;
  recipients: EmailRecipient[];
  onAddRecipient: (recipient: EmailRecipient) => void;
  onRemoveRecipient: (index: number) => void;
  placeholder?: string;
  variant?: 'primary' | 'secondary' | 'success';
  required?: boolean;
  error?: string;
}

const RecipientInput: React.FC<RecipientInputProps> = ({
  label,
  recipients,
  onAddRecipient,
  onRemoveRecipient,
  placeholder = "Enter email address and press Enter",
  variant = 'primary',
  required = false,
  error
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addRecipient();
    }
  };

  const addRecipient = () => {
    const email = inputValue.trim();
    if (email && email.includes('@')) {
      const recipient: EmailRecipient = { email };
      onAddRecipient(recipient);
      setInputValue('');
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addRecipient();
    }
  };

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center">
        <div className="flex-shrink-0 me-2" style={{ width: '32px' }}>
          <span className="text-muted fw-normal" style={{ fontSize: '0.9rem' }}>
            {label}:
          </span>
        </div>
        <div 
          className={classNames(
            'border rounded d-flex flex-wrap align-items-center flex-grow-1 px-2 py-1',
            error ? 'border-danger' : 'border-light'
          )}
          style={{ minHeight: '38px', backgroundColor: '#f8f9fa' }}
        >
          {recipients.map((recipient, index) => (
            <RecipientChip
              key={`${recipient.email}-${index}`}
              recipient={recipient}
              onRemove={() => onRemoveRecipient(index)}
              variant={variant}
            />
          ))}
          <Form.Control
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={recipients.length === 0 ? placeholder : ""}
            className="border-0 bg-transparent flex-grow-1 p-0"
            style={{ 
              minWidth: '150px', 
              boxShadow: 'none',
              fontSize: '0.9rem'
            }}
          />
        </div>
      </div>
      {error && <small className="text-danger ms-5">{formatErrorForReact(error)}</small>}
    </div>
  );
};

const EmailDetailModal = ({ show, onHide, activityData, originalActivityItem, isLoadingDetails = false }: EmailDetailModalProps) => {
  console.log('EmailDetailModal render:', { show, activityData, isLoadingDetails }); // Debug log
  
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [replyToRecipients, setReplyToRecipients] = useState<EmailRecipient[]>([]);
  const [replyCcRecipients, setReplyCcRecipients] = useState<EmailRecipient[]>([]);
  const [replyBccRecipients, setReplyBccRecipients] = useState<EmailRecipient[]>([]);
  const [showBccReply, setShowBccReply] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<EmailFormInputs>();

  const watchReplyMessage = watch("message", "");
  
  if (!activityData || !activityData.content.email) {
    console.log('EmailDetailModal: No data or email content'); // Debug log
    return null;
  }

  // Get email data from activityData.content
  const { email } = activityData.content as { email: EmailContent };
  
  // Process attachments that might come from fetchEmails
  // This handles both standard and Gmail attachment formats (Gmail uses 'parts' instead of 'attachments')
  const attachments: EmailAttachment[] = activityData.attachments || [];
  console.log('Email attachments:', attachments, email); // Debug log for attachments
  
  // Get the mail_id needed for attachment download URLs (could be in different places depending on source)
  const mailId = email.mail_id || (originalActivityItem && originalActivityItem.mail_id);
    
  // Format file size for display
  const formatSize = (bytes: number) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleOpenInMail = () => {
    const subject = encodeURIComponent(email.subject);
    const body = encodeURIComponent(email.body);
    const to = encodeURIComponent(email.to);
    const cc = email.cc ? encodeURIComponent(email.cc) : '';
    
    let mailtoLink = `mailto:${to}?subject=${subject}&body=${body}`;
    if (cc) {
      mailtoLink += `&cc=${cc}`;
    }
    
    window.location.href = mailtoLink;
  };

  const handleReplyClick = () => {
    setShowReplyComposer(true);
    setValue('subject', `Re: ${email.subject}`);
    
    // Initialize reply recipients
    setReplyToRecipients([{ email: email.from }]);
    setReplyCcRecipients([]);
    setReplyBccRecipients([]);
    setShowBccReply(false);
  };

  const recipientsToString = (recipients: EmailRecipient[]) => {
    return recipients.map(r => r.email).join(', ');
  };

  const handleFileAttachment = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setReplyAttachments(prev => [...prev, ...fileArray]);
    }
  };

  const removeAttachment = (index: number) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onSubmitReply: SubmitHandler<EmailFormInputs> = async (data) => {
    try {
      // Validate that we have at least one TO recipient
      if (replyToRecipients.length === 0) {
        toast.error('Please add at least one recipient');
        return;
      }

      const replyData = {
        ...data,
        to: recipientsToString(replyToRecipients),
        cc: replyCcRecipients.length > 0 ? recipientsToString(replyCcRecipients) : undefined,
        bcc: replyBccRecipients.length > 0 ? recipientsToString(replyBccRecipients) : undefined,
        threadId: originalActivityItem?.thread_id,
        messageId: originalActivityItem?.mail_id || activityData?.id,
      };

      console.log('📤 Sending reply from EmailDetailModal:', replyData);
      
      // Make actual API call to send reply
      const response = await axiosInstance.post('emails/reply', replyData);
      
      console.log('✅ Reply sent successfully:', response.data);
      toast.success('Reply sent successfully!');
      
      reset();
      setShowReplyComposer(false);
      
      // Reset reply recipients
      setReplyToRecipients([]);
      setReplyCcRecipients([]);
      setReplyBccRecipients([]);
      setShowBccReply(false);
      setReplyAttachments([]);
      
      onHide(); // Close modal after sending
    } catch (error: any) {
      console.error('❌ Error sending reply from EmailDetailModal:', error.response?.data || error);
      
      // Show specific error message from backend
      const errorMessage = error.response?.data?.message || 'Failed to send reply';
      toast.error(errorMessage);
      
      // Check if it's an authentication issue
      if (error.response?.status === 401 || errorMessage.includes('Email not connected')) {
        toast.error('Please reconnect your email account in Settings');
      }
    }
  };

  const handleGenerateAIDraft = async () => {
    setIsGeneratingDraft(true);
    try {
      const aiReply = `Thank you for your email. I've reviewed your message and will get back to you with the information you requested.\n\nBest regards,\n[Your Name]`;
      
      setValue('message', aiReply);
      toast.success('AI reply generated successfully!');
    } catch (error) {
      toast.error('Failed to generate AI reply');
    } finally {
      setIsGeneratingDraft(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      className="email-detail-modal"
      style={{ 
        zIndex: 1060
      }}
      backdrop="static" // Prevent backdrop clicks that could cause layout shifts
    >
      <Modal.Header closeButton className="border-0 pb-0" style={{
        minHeight: '70px', // Reserve space for header to prevent CLS
        transition: 'all 0.2s ease'
      }}>
        <Modal.Title className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center justify-content-center bg-primary rounded-circle" 
               style={{ width: '32px', height: '32px' }}>
            <UilMessage size="18" className="text-white" />
          </div>
          <span className="fw-bold text-dark">Email Details</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0" style={{
        minHeight: '500px', // Increased from 400px to give more space for email content
        maxHeight: '85vh',   // Increased from 80vh to allow more vertical space
        overflowY: 'auto',   // Allow scrolling if content is too tall
        transition: 'min-height 0.2s ease'
      }}>
        <div className="modern-email-interface" style={{
          // Ensure consistent layout during content transitions
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Reply Composer - Shows at top when replying */}
          {showReplyComposer && (
            <div className="reply-composer-section" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <Card className="reply-composer-card m-4 mb-0 shadow-lg border-0">
                <Card.Header className="bg-transparent border-0 p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 d-flex align-items-center gap-3 text-white">
                      <div className="d-flex align-items-center justify-content-center bg-white bg-opacity-20 rounded-circle" 
                           style={{ width: '40px', height: '40px' }}>
                        <FontAwesomeIcon icon={faReply} size="lg" />
                      </div>
                      <div>
                        <div className="fw-bold">Reply to {email.from}</div>
                        <small className="text-white-50">Compose your response</small>
                      </div>
                    </h5>
                    <Button
                      variant="link"
                      size="lg"
                      className="text-white text-white-50-hover p-0"
                      onClick={() => {
                        setShowReplyComposer(false);
                        reset();
                      }}
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-4 bg-white">
                  <Form onSubmit={handleSubmit(onSubmitReply)}>
                    <div className="condensed-email-form">
                      {/* TO and CC Fields */}
                      <div className="recipient-fields mb-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="flex-grow-1">
                            <RecipientInput
                              label="To"
                              recipients={replyToRecipients}
                              onAddRecipient={(recipient) => setReplyToRecipients([...replyToRecipients, recipient])}
                              onRemoveRecipient={(index) => setReplyToRecipients(replyToRecipients.filter((_, i) => i !== index))}
                              placeholder="Enter recipient email address"
                              variant="primary"
                              required
                              error={errors.to?.message}
                            />
                          </div>
                          {!showBccReply && (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-decoration-none ms-3 p-0 text-primary"
                              onClick={() => setShowBccReply(true)}
                              style={{ fontSize: '0.9rem', fontWeight: '500' }}
                            >
                              + BCC
                            </Button>
                          )}
                        </div>
                        
                        <div className="mb-3">
                          <RecipientInput
                            label="Cc"
                            recipients={replyCcRecipients}
                            onAddRecipient={(recipient) => setReplyCcRecipients([...replyCcRecipients, recipient])}
                            onRemoveRecipient={(index) => setReplyCcRecipients(replyCcRecipients.filter((_, i) => i !== index))}
                            placeholder="Add CC recipients"
                            variant="secondary"
                          />
                        </div>
                        
                        {showBccReply && (
                          <div className="mb-3">
                            <RecipientInput
                              label="Bcc"
                              recipients={replyBccRecipients}
                              onAddRecipient={(recipient) => setReplyBccRecipients([...replyBccRecipients, recipient])}
                              onRemoveRecipient={(index) => setReplyBccRecipients(replyBccRecipients.filter((_, i) => i !== index))}
                              placeholder="Add BCC recipients"
                              variant="success"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Subject Field */}
                      <div className="mb-4">
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0" style={{ minWidth: '80px' }}>
                            <FontAwesomeIcon icon={faEnvelope} className="text-muted me-2" />
                            Subject
                          </span>
                          <Form.Control
                            type="text"
                            placeholder="Email subject"
                            {...register("subject", { required: "Subject is required" })}
                            className="border-start-0 bg-light"
                            style={{ fontSize: '0.95rem' }}
                          />
                        </div>
                        {errors.subject && (
                          <small className="text-danger mt-1">{errors.subject.message}</small>
                        )}
                      </div>
                      
                      <Row>
                        <Col md="12">
                          <div className="mb-4">
                            <label className="form-label fw-semibold text-dark mb-3">
                              <FontAwesomeIcon icon={faPaperPlane} className="me-2 text-primary" />
                              Message
                            </label>
                            <div className="border rounded" style={{ minHeight: '250px' }}>
                              <ReactQuill
                                style={{ height: "200px" }}
                                theme="snow"
                                value={watchReplyMessage}
                                onChange={(value) => setValue("message", value)}
                                placeholder="Write your reply here..."
                                modules={{
                                  toolbar: [
                                    ['bold', 'italic', 'underline'],
                                    ['link'],
                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                    ['clean']
                                  ],
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                        
                        {/* Attachments Section */}
                        <Col md="12">
                          <div className="mb-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <label className="form-label fw-semibold text-dark mb-0">
                                <FontAwesomeIcon icon={faPaperclip} className="me-2 text-secondary" />
                                Attachments
                              </label>
                              <div>
                                <input
                                  type="file"
                                  multiple
                                  onChange={(e) => handleFileAttachment(e.target.files)}
                                  style={{ display: 'none' }}
                                  id="reply-file-input"
                                />
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => document.getElementById('reply-file-input')?.click()}
                                  className="d-flex align-items-center gap-2 px-3"
                                >
                                  <FontAwesomeIcon icon={faPaperclip} size="sm" />
                                  Browse Files
                                </Button>
                              </div>
                            </div>
                            
                            {replyAttachments.length > 0 && (
                              <div className="attachment-list">
                                {replyAttachments.map((file, index) => (
                                  <div key={index} className="attachment-item d-flex align-items-center justify-content-between p-3 border rounded mb-2 bg-light">
                                    <div className="d-flex align-items-center gap-3">
                                      <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded" 
                                           style={{ width: '40px', height: '40px' }}>
                                        <FontAwesomeIcon icon={faPaperclip} className="text-primary" />
                                      </div>
                                      <div>
                                        <span className="fw-medium text-dark d-block" style={{ fontSize: '0.9rem' }}>{file.name}</span>
                                        <small className="text-muted">({formatFileSize(file.size)})</small>
                                      </div>
                                    </div>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => removeAttachment(index)}
                                      className="text-danger p-0"
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </Col>
                        
                        <Col md="12">
                          <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                            <Button
                              variant="outline-primary"
                              onClick={handleGenerateAIDraft}
                              disabled={isGeneratingDraft}
                              className="d-flex align-items-center gap-2 px-4"
                            >
                              <FontAwesomeIcon icon={faBrain} />
                              {isGeneratingDraft ? 'Generating...' : 'AI Assist'}
                            </Button>
                            
                            <div className="d-flex gap-3">
                              <Button 
                                variant="outline-secondary" 
                                onClick={() => {
                                  setShowReplyComposer(false);
                                  reset();
                                }}
                                className="px-4"
                              >
                                Cancel
                              </Button>
                              <Button 
                                variant="primary" 
                                onClick={handleSubmit(onSubmitReply)}
                                disabled={!watchReplyMessage.trim()}
                                className="px-4 d-flex align-items-center gap-2"
                                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                              >
                                <FontAwesomeIcon icon={faPaperPlane} />
                                Send Reply
                              </Button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Email Thread Content */}
          <div className="email-thread-content p-4">
            <div className="email-thread-container">
              {/* Enhanced Email Header */}
              <div className="email-header-section mb-4">
                <div className="d-flex align-items-start gap-4">
                  {/* Sender Avatar */}
                  <div className="sender-avatar-container">
                    <div className="sender-avatar d-flex align-items-center justify-content-center bg-primary text-white fw-bold rounded-circle shadow" 
                         style={{ width: '48px', height: '48px', fontSize: '1.1rem' }}>
                      {email.from.charAt(0).toUpperCase()}
                    </div>
                    <div className="avatar-status bg-success rounded-circle position-absolute" 
                         style={{ width: '14px', height: '14px', bottom: '2px', right: '2px', border: '2px solid white' }}></div>
                  </div>
                  
                  {/* Email Metadata */}
                  <div className="email-metadata flex-grow-1" style={{ overflow: 'hidden', width: '100%' }}>
                    <div className="d-flex align-items-center justify-content-between mb-2 email-header-row">
                      <div className="email-sender-info" style={{ width: '100%', overflow: 'hidden' }}>
                        <h4 className="sender-name mb-1 fw-bold text-dark" style={{ fontSize: 'clamp(0.95rem, 4vw, 1.1rem)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.from}</h4>
                        <div className="email-details text-muted" style={{ width: '100%', overflow: 'hidden' }}>
                          <div className="d-flex mb-1" style={{ fontSize: 'clamp(0.75rem, 3vw, 0.85rem)', width: '100%' }}>
                            <div style={{ width: '30px', minWidth: '30px', flexShrink: 0 }}>
                              <span className="fw-medium">To:</span>
                            </div>
                            <div style={{ width: 'calc(100% - 30px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {email.to}
                            </div>
                          </div>
                          {email.cc && (
                            <div className="d-flex mb-1" style={{ fontSize: 'clamp(0.75rem, 3vw, 0.85rem)', width: '100%' }}>
                              <div style={{ width: '30px', minWidth: '30px', flexShrink: 0 }}>
                                <span className="fw-medium">CC:</span>
                              </div>
                              <div style={{ width: 'calc(100% - 30px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {email.cc}
                              </div>
                            </div>
                          )}
                          <div className="email-metadata-time d-flex flex-wrap align-items-center text-muted" style={{ fontSize: 'clamp(0.75rem, 3vw, 0.85rem)' }}>
                            <div className="d-flex align-items-center me-3">
                              <FontAwesomeIcon icon={faCalendarAlt} size="sm" className="me-1" />
                              <span>{formatDate(activityData?.oppositeContent?.date || '')}</span>
                            </div>
                            <div className="d-flex align-items-center me-3">
                              <FontAwesomeIcon icon={faClock} size="sm" className="me-1" />
                              <span>{activityData?.oppositeContent?.time || ''}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons in Header */}
                      <div className="d-flex gap-2 email-header-actions">
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={handleReplyClick}
                          className="d-flex align-items-center gap-2 px-3"
                          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                        >
                          <FontAwesomeIcon icon={faReply} />
                          Reply
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          className="d-flex align-items-center gap-2 px-3"
                        >
                          <FontAwesomeIcon icon={faForward} />
                          Forward
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subject Section */}
              <div className="email-subject-section mb-3">
                <div className="bg-light rounded p-3 border-start border-primary border-4 subject-container">
                  <h4 className="mb-0 fw-bold text-dark email-subject-title" style={{ 
                    fontSize: 'clamp(1rem, 4vw, 1.2rem)',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                  }}>
                    {email.subject}
                  </h4>
                </div>
              </div>

              {/* Email Body Section */}
              <div className="email-body-section">
                <Card className="border-0 shadow-sm">
                  <Card.Body className="p-0">
                    <div 
                      className="email-content-body"
                      style={{ 
                        lineHeight: 1.5,
                        fontSize: '1rem',
                        color: '#2c3e50',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        overflow: 'visible', // Changed from 'auto' to allow full content visibility
                        minHeight: '150px', // Reserve minimum space to prevent CLS
                        wordBreak: 'break-word',
                        padding: '8px 12px',
                        transition: 'min-height 0.2s ease' // Smooth transition
                      }}
                    >
                      <style>
                        {`
                          .email-content-body a {
                            color: #0066cc !important;
                            text-decoration: underline !important;
                            font-weight: 600 !important;
                            background-color: rgba(0, 102, 204, 0.05) !important;
                            padding: 0 3px !important;
                            border-radius: 2px !important;
                            transition: all 0.2s ease !important;
                          }
                          .email-content-body a:hover {
                            color: #004080 !important;
                            background-color: rgba(0, 102, 204, 0.1) !important;
                            text-decoration: underline !important;
                          }
                          .email-content-body .email-link {
                            display: inline-block !important;
                            background: #e6f7ff !important;
                            color: #0066cc !important;
                            font-weight: bold !important;
                            padding: 3px 8px !important;
                            margin: 2px 0 !important;
                            border-radius: 4px !important;
                            text-decoration: none !important;
                            box-shadow: 0 1px 2px rgba(0,0,0,0.1) !important;
                          }
                          .email-content-body .email-link:hover {
                            background: #bae7ff !important;
                            color: #0050b3 !important;
                          }
                          /* CLS Prevention - Consistent heights for skeleton elements */
                          .email-body-skeleton {
                            min-height: 150px;
                            animation: fadeIn 0.3s ease;
                          }
                          .skeleton-element {
                            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                            background-size: 200% 100%;
                            animation: shimmer 1.5s infinite;
                            border-radius: 4px;
                          }
                          @keyframes shimmer {
                            0% { background-position: -200% 0; }
                            100% { background-position: 200% 0; }
                          }
                          @keyframes fadeIn {
                            from { opacity: 0; }
                            to { opacity: 1; }
                          }
                          /* Smooth content transitions */
                          .email-content-transition {
                            transition: opacity 0.2s ease, transform 0.2s ease;
                          }
                          .email-content-loading {
                            opacity: 0.7;
                            transform: translateY(2px);
                          }
                          .email-content-loaded {
                            opacity: 1;
                            transform: translateY(0);
                          }
                        `}
                      </style>
                      <div 
                        className="email-content email-html-content"
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(
                            (() => {
                              // Get raw email body with enhanced fallback
                              let rawBody = email.isCompressed ? 
                                decodeEmailBody(email.body, true) : 
                                (email.body || '');

                              // Check if we have any content at all
                              if (!rawBody || rawBody.trim() === '' || rawBody === '(No content)') {
                                console.log('📧 [EmailDetailModal] No email content available, showing fallback message');
                                return `
                                  <div class="email-no-content-container" style="
                                    padding: 40px 20px;
                                    text-align: center;
                                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                                    border: 2px dashed #dee2e6;
                                    border-radius: 12px;
                                    margin: 20px 0;
                                  ">
                                    <div style="
                                      font-size: 48px;
                                      color: #6c757d;
                                      margin-bottom: 16px;
                                    ">📧</div>
                                    <h4 style="
                                      color: #495057;
                                      margin-bottom: 12px;
                                      font-weight: 600;
                                    ">Email Content Not Available</h4>
                                    <p style="
                                      color: #6c757d;
                                      margin-bottom: 20px;
                                      line-height: 1.5;
                                    ">
                                      This email's content wasn't captured during sync.<br>
                                      Subject: <strong>${email.subject || '(No Subject)'}</strong><br>
                                      From: <strong>${email.from || 'Unknown'}</strong>
                                    </p>
                                    <div style="
                                      padding: 12px 20px;
                                      background: #fff3cd;
                                      border: 1px solid #ffeaa7;
                                      border-radius: 8px;
                                      color: #856404;
                                      font-size: 14px;
                                    ">
                                      💡 <strong>Tip:</strong> You can view this email in your Gmail or email client using the subject line above.
                                    </div>
                                  </div>
                                `;
                              }

                              // If it's a full HTML document, preserve it as HTML (like Email tab does)
                              if (rawBody.includes('<html') || rawBody.includes('DOCTYPE') || rawBody.includes('<body')) {
                                console.log('📧 [EmailDetailModal] Rendering HTML email content');
                                return rawBody; // Preserve HTML structure like Email tab
                              }

                              // For plain text emails, process URLs and convert newlines
                              console.log('📧 [EmailDetailModal] Rendering plain text email content');
                              return rawBody
                                .replace(/\n/g, '<br>')
                                .replace(
                                  /(\b(?:localhost:[0-9]+\/[^\s<]*|https?:\/\/[^\s<]+)\b)/g,
                                  (match: string) => {
                                    const isLocalhost = match.startsWith('localhost');
                                    const href = isLocalhost ? `http://${match}` : match;
                                    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="email-link">${match}</a>`;
                                  }
                                );
                            })(),
                            {
                              ADD_ATTR: ['style', 'target', 'rel', 'class', 'href', 'bgcolor', 'align', 'valign', 'width', 'height', 'cellpadding', 'cellspacing', 'border'],
                              ADD_TAGS: ['style', 'table', 'tbody', 'thead', 'tr', 'td', 'th', 'img'],
                              ALLOWED_TAGS: ['a', 'b', 'br', 'div', 'em', 'i', 'li', 'ol', 'p', 'span', 'strong', 'u', 'ul', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'tbody', 'thead', 'tr', 'td', 'th', 'img', 'style'],
                              ALLOWED_ATTR: ['href', 'style', 'target', 'rel', 'class', 'src', 'alt', 'bgcolor', 'align', 'valign', 'width', 'height', 'cellpadding', 'cellspacing', 'border']
                            }
                          )
                        }}
                        style={{ 
                          lineHeight: 1.6,
                          fontSize: '14px',
                          color: '#2c3e50',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          wordWrap: 'break-word'
                        }}
                      />
                    
                    </div>
                  </Card.Body>
                </Card>
              </div>

                            {/* Global skeleton animation - Removed duplicate, handled in main styles above */}
              
              {/* Email Attachments Section */}
              {(isLoadingDetails || (attachments && attachments.length > 0)) && (
                <div className="email-attachments-section mb-4 mt-4">
                  <div className="bg-light rounded p-3 border" style={{
                    minHeight: '120px', // Reserve minimum space to prevent CLS
                    transition: 'min-height 0.2s ease'
                  }}>
                    {isLoadingDetails ? (
                      // Show skeleton while loading attachment details
                      <div className="attachments-skeleton" style={{ minHeight: '100px' }}>
                        <h5 className="mb-3 d-flex align-items-center">
                          <div className="skeleton-element me-2" style={{ width: '16px', height: '16px', borderRadius: '4px' }}></div>
                          <div className="skeleton-element" style={{ width: '120px', height: '20px' }}></div>
                        </h5>
                        <div className="attachment-list d-flex flex-wrap gap-3" style={{
                          minHeight: '60px' // Ensure consistent height for attachment area
                        }}>
                          {/* Show skeleton attachment items with consistent dimensions */}
                          {[1, 2, 3].map((_, index) => (
                            <div key={index} className="attachment-item" style={{
                              width: '220px', // Fixed width to prevent layout shifts
                              height: '60px',  // Fixed height to prevent layout shifts
                              display: 'flex'
                            }}>
                              <div className="d-flex align-items-center p-2 border rounded bg-white w-100">
                                <div className="file-icon d-flex align-items-center justify-content-center p-3 bg-light rounded me-2" style={{
                                  width: '48px', // Fixed dimensions
                                  height: '48px',
                                  flexShrink: 0
                                }}>
                                  <div className="skeleton-element" style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px'
                                  }}></div>
                                </div>
                                <div className="file-details overflow-hidden flex-grow-1">
                                  <div className="skeleton-element mb-1" style={{ width: '140px', height: '14px' }}></div>
                                  <div className="skeleton-element" style={{ width: '80px', height: '12px' }}></div>
                                </div>
                                <div className="skeleton-element" style={{ 
                                  width: '20px', 
                                  height: '20px', 
                                  borderRadius: '4px',
                                  flexShrink: 0
                                }}></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      attachments && attachments.length > 0 && (
                        <div className="attachments-content email-content-transition email-content-loaded" style={{
                          minHeight: '100px' // Match skeleton height
                        }}>
                          <h5 className="mb-3 d-flex align-items-center" style={{ height: '28px' }}> {/* Fixed header height */}
                            <FontAwesomeIcon icon={faPaperclip} className="me-2 text-primary" />
                            <span className="fw-bold">Attachments ({attachments.length})</span>
                          </h5>
                          <div className="attachment-list d-flex flex-wrap gap-3" style={{
                            minHeight: '60px' // Match skeleton attachment area height
                          }}>
                            {attachments.map((attachment, index) => {
                              // Skip loading attachments
                              if (attachment.isLoading) return null;
                              
                              // For normal attachments
                              let fileIcon = faFile;
                              const mimeType = attachment.mimeType?.toLowerCase() || '';
                              
                              if (mimeType.includes('pdf')) fileIcon = faFilePdf;
                              else if (mimeType.includes('image/')) fileIcon = faFileImage;
                              else if (mimeType.includes('zip') || mimeType.includes('compressed')) fileIcon = faFileArchive;
                              else if (mimeType.includes('html') || mimeType.includes('javascript') || mimeType.includes('json')) fileIcon = faFileCode;
                              else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) fileIcon = faFileExcel;
                              else if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) fileIcon = faFilePowerpoint;
                              else if (mimeType.includes('word') || mimeType.includes('document')) fileIcon = faFileWord;
                              else fileIcon = faFileAlt;
                              
                              // Get filename - handle both standard and Gmail formats
                              const displayFilename = attachment.filename || attachment.name || `attachment-${index + 1}`;
                              const displaySize = attachment.size || 0;
                              
                              return (
                                <div key={attachment.id || attachment.attachmentId || index} 
                                     className="attachment-item" 
                                     style={{
                                       width: '220px', // Match skeleton dimensions
                                       height: '60px',  // Match skeleton dimensions
                                       display: 'flex'
                                     }}>
                                  <div className="d-flex align-items-center p-2 border rounded bg-white w-100">
                                    <div className="file-icon d-flex align-items-center justify-content-center p-3 bg-light rounded me-2" style={{
                                      width: '48px', // Match skeleton dimensions
                                      height: '48px',
                                      flexShrink: 0
                                    }}>
                                      <FontAwesomeIcon icon={fileIcon} size="lg" className="text-primary" />
                                    </div>
                                    <div className="file-details overflow-hidden flex-grow-1">
                                      <div className="file-name text-truncate fw-bold" style={{ fontSize: '0.9rem', height: '14px', lineHeight: '14px' }}>
                                        {displayFilename}
                                      </div>
                                      <div className="file-size text-muted" style={{ fontSize: '0.8rem', height: '12px', lineHeight: '12px' }}>
                                        {formatSize(displaySize)}
                                      </div>
                                    </div>
                                    <div style={{ width: '32px', flexShrink: 0 }}> {/* Fixed width for button area */}
                                      {attachment.data || attachment.attachmentId ? (
                                        <button 
                                          className="btn btn-sm text-primary border-0 bg-transparent"
                                          title="Download attachment"
                                          style={{ width: '24px', height: '24px', padding: 0 }}
                                          onClick={() => {
                                            // If we have a mail_id and attachmentId, we can construct a download URL
                                            // mailId is already defined at the component level
                                            if (mailId && attachment.attachmentId) {
                                              const downloadUrl = `/api/email/attachment/${mailId}/${attachment.attachmentId}`;
                                              window.open(downloadUrl, '_blank');
                                            } else if (attachment.data) {
                                              // If we have the attachment data directly, create a download link
                                              // Handle base64 data if available
                                              if (typeof attachment.data === 'string') {
                                                // Convert base64 to binary using browser-native methods
                                                const binaryString = atob(attachment.data.replace(/-/g, '+').replace(/_/g, '/'));
                                                const len = binaryString.length;
                                                const bytes = new Uint8Array(len);
                                                for (let i = 0; i < len; i++) {
                                                  bytes[i] = binaryString.charCodeAt(i);
                                                }
                                                
                                                const blob = new Blob(
                                                  [bytes],
                                                  { type: attachment.mimeType || 'application/octet-stream' }
                                                );
                                                const url = URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = displayFilename;
                                                a.click();
                                                URL.revokeObjectURL(url);
                                              }
                                            }
                                          }}
                                        >
                                          <FontAwesomeIcon icon={faDownload} />
                                        </button>
                                      ) : (
                                        <span className="text-muted" title="Download not available" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <FontAwesomeIcon icon={faDownload} opacity={0.5} />
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Email Footer Actions */}
              <div className="email-footer-actions mt-3 pt-3 border-top">
                <div className="d-flex justify-content-end flex-wrap gap-2">
                  <Button 
                    variant="primary" 
                    onClick={handleReplyClick}
                    className="d-flex align-items-center gap-1 px-2 py-1"
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}
                  >
                    <FontAwesomeIcon icon={faReply} size="xs" style={{ fontSize: '0.7rem' }} />
                    <span>Reply</span>
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    className="d-flex align-items-center gap-1 px-2 py-1"
                    style={{ 
                      borderRadius: '6px', 
                      fontSize: '0.8rem', 
                      fontWeight: '500' 
                    }}
                  >
                    <FontAwesomeIcon icon={faForward} size="xs" style={{ fontSize: '0.7rem' }} />
                    Forward
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EmailDetailModal; 